/**
 * Document rename — the Obsidian "rename a note, links follow" engine.
 *
 * Renaming a `.md` document changes the name refs use (`[[Name]]` /
 * `[[Name#sec]]`) and updates every inbound reference across the workspace, plus
 * the document's own identity:
 *   - frontmatter `title:` present → rewrite the title (name = title);
 *   - else → rename the file (name = file stem).
 *
 * Ref rewriting reuses core (`splitCodeRegions` shields code/calc; `scanRefs` +
 * `classifyRef` find the exact `[[…]]` spans) so we only touch real references,
 * never text inside code or a coincidental substring.
 *
 * `computeDocRename` is pure: (files, oldName, newName, docPath) → the edits +
 * an optional file rename. The caller applies them to the store.
 */
import { splitCodeRegions, scanRefs, classifyRef } from "@archlang/engine";

export interface DocRenamePlan {
  /** path → new content, for every file whose refs changed (+ the doc itself). */
  readonly edits: ReadonlyMap<string, string>;
  /** A file rename when the doc has no frontmatter title (name = stem). */
  readonly rename?: { readonly from: string; readonly to: string };
}

/** Rewrite the `[[…]]` references to `oldName` inside one text body. */
function rewriteRefs(body: string, oldName: string, newName: string): string {
  let out = "";
  let cursor = 0;
  for (const seg of splitCodeRegions(body)) {
    const segStart = cursor;
    cursor += seg.text.length;
    if (seg.kind !== "text") {
      out += seg.text;
      continue;
    }
    let segOut = "";
    let local = 0;
    for (const tok of scanRefs(seg.text)) {
      if (tok.kind !== "ref") continue;
      const c = classifyRef(tok.inner);
      const hit =
        (c.kind === "name" && c.name === oldName) ||
        (c.kind === "section" && c.doc === oldName);
      if (!hit) continue;
      // Append text up to this ref, then the rewritten ref.
      segOut += seg.text.slice(local, tok.index);
      const newInner =
        c.kind === "section" ? `${newName}#${c.section}` : newName;
      segOut += `[[${newInner}]]`;
      local = tok.index + tok.raw.length;
    }
    segOut += seg.text.slice(local);
    out += segOut;
    void segStart;
  }
  return out;
}

/**
 * Replace a flat frontmatter `title:` value (first occurrence), or null when
 * the doc has no usable title — in which case its NAME is its filename stem.
 *
 * This null/non-null answer is the single source of truth for "is this document
 * named by a title?": `computeDocRename` uses it to decide between rewriting the
 * title and renaming the file, and `renameDocFile` reads that decision off the
 * plan. Every attempt to answer the same question a second way (a
 * `parseFrontmatter` check next to this one) disagreed with it in some corner
 * and produced BOTH mutations at once.
 */
function rewriteTitle(body: string, newName: string): string | null {
  if (!(body.startsWith("---\n") || body.startsWith("---\r\n"))) return null;
  const close = body.indexOf("\n---", 3);
  if (close === -1) return null;
  const head = body.slice(0, close);
  const rest = body.slice(close);
  // `[ \t]`, NOT `\s`: `\s` spans newlines, so a BLANK `title:` made `(.*)`
  // match the NEXT frontmatter line and replace it with the new name — silently
  // destroying `author: nick`.
  const match = /^title:[ \t]*(.*)$/m.exec(head);
  // A blank title is not a name. The doc is named by its stem, exactly as if
  // the key were absent — so the caller renames the file and carries the refs.
  if (!match || match[1]!.trim() === "") return null;
  const replaced = head.replace(
    /^(title:[ \t]*)(.*)$/m,
    (_m, p1: string) => `${p1}${/[:#]/.test(newName) ? JSON.stringify(newName) : newName}`,
  );
  if (replaced === head) return null; // no title line
  return replaced + rest;
}

const TEXT_EXT = /\.(md|markdown|arch|archspace)$/i;

export function computeDocRename(
  files: ReadonlyMap<string, string>,
  oldName: string,
  newName: string,
  docPath: string,
): DocRenamePlan {
  const edits = new Map<string, string>();
  if (oldName === newName || !newName.trim()) return { edits };

  const docBody = files.get(docPath) ?? "";
  const titled = rewriteTitle(docBody, newName); // non-null iff the doc has a title

  // Rewrite inbound refs in every workspace text file.
  for (const [path, body] of files) {
    if (!TEXT_EXT.test(path)) continue;
    const base = path === docPath && titled !== null ? titled : body;
    const next = rewriteRefs(base, oldName, newName);
    if (next !== body) edits.set(path, next);
  }
  // If the doc body only changed via its title (no self-ref), still record it.
  if (titled !== null && titled !== docBody && !edits.has(docPath)) {
    edits.set(docPath, rewriteRefs(titled, oldName, newName));
  }

  // No title → the name is the filename stem; rename the file.
  if (titled === null) {
    const slash = docPath.lastIndexOf("/");
    const dir = docPath.slice(0, slash + 1);
    const ext = docPath.slice(docPath.lastIndexOf("."));
    const to = `${dir}${newName}${ext}`;
    if (to !== docPath) return { edits, rename: { from: docPath, to } };
  }
  return { edits };
}
